import { useEffect, useState } from 'react';
import { formatDuration } from '../lib/protocol.js';
import type { Jam } from '../lib/useJam.js';

type Props = { jam: Jam };

/**
 * 曲の追加。
 *
 * リンクを貼った場合は自動で1件に解決してそのまま追加する（Spotify / Apple Music
 * のリンクは曲名とアーティスト名から YouTube 上の同一曲に突き合わせる）。
 * 曲名だけを入れた場合は候補を出して選ばせる。ライブ版やカバー版を勝手に
 * 掴む事故が起きやすいので、そこは投稿者の判断に委ねる。
 */
export function AddBar({ jam }: Props) {
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const results = jam.searchResults;

  // 追加が反映されるか候補が返ってきたら入力欄を解放する
  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => setPending(false), 8000);
    return () => window.clearTimeout(timer);
  }, [pending]);

  useEffect(() => {
    if (results) setPending(false);
  }, [results]);

  const queueLength = jam.state?.queue.length ?? 0;
  useEffect(() => {
    setPending(false);
  }, [queueLength]);

  const submit = () => {
    const value = input.trim();
    if (!value) return;
    jam.actions.add(value);
    setInput('');
    setPending(true);
  };

  return (
    <>
      <section className="addbar">
        <input
          className="input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          placeholder="リンクを貼る、または曲名で検索"
          autoComplete="off"
        />
        <button className="btn btn-primary" onClick={submit} disabled={!input.trim()}>
          追加
        </button>
      </section>
      {pending && <p className="hint hint-small">解決中...</p>}

      {results && (
        <div className="overlay" onClick={() => jam.clearSearchResults()}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <header className="sheet-head">
              <h3>「{results.query}」の候補</h3>
              <button className="icon-btn" onClick={() => jam.clearSearchResults()}>
                ×
              </button>
            </header>
            {results.results.length === 0 ? (
              <p className="empty">見つかりませんでした</p>
            ) : (
              <ul className="track-list">
                {results.results.map((candidate) => (
                  <li key={candidate.videoId} className="track-row track-row-clickable">
                    <button
                      className="candidate-btn"
                      onClick={() => {
                        jam.actions.addCandidate(candidate);
                        jam.clearSearchResults();
                      }}
                    >
                      {candidate.thumbnail ? (
                        <img src={candidate.thumbnail} alt="" className="track-thumb" />
                      ) : (
                        <div className="track-thumb track-thumb-empty" />
                      )}
                      <div className="track-row-main">
                        <div className="track-row-title">{candidate.title}</div>
                        <div className="track-row-sub">
                          {candidate.artist ?? '不明'}
                          {candidate.durationMs > 0 &&
                            ` · ${formatDuration(candidate.durationMs)}`}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
